// React Dependencies
import React from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import QuillResize from 'quill-resize-module';
import { v4 as uuidV4 } from 'uuid';

// Internal Dependencies
import { IsEmpty } from '../../../common/checks';

Quill.register('modules/resize', QuillResize);

const inputFile = React.createRef(null);

class Editor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      description: this.props.description,
      instanceId:  `quill-bounds-${uuidV4()}`,
    };
  }

  modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        [
          'link',
          this.props.allowUpload ? 'image' : null,
        ].filter((item) => !IsEmpty(item)),
        ['clean'],
      ],
      handlers: {
        image: () => {
          inputFile.current.click();
        },
      },
    },
    resize: {
      parchment: {
        image: {
          attribute: ['width', 'height'],
          limit:     {
            minWidth:  100,
            maxWidth:  800,
            minHeight: 75,
            maxHeight: 600,
            ratio:     0.75, // Uncomment to Disable Free-Form
          },
        },
      },
      modules: ['Resize', 'DisplaySize'],
      styles:  {
        display: {
          backgroundColor: 'black',
          border:          'none',
          color:           'white',
        },
      },
    },
  }

  acceptedFormats = [
    'background', 'bold', 'color', 'font', 'code', 'italic', 'link', 'size',
    'strike', 'script', 'underline', 'blockquote', 'header', 'indent', 'list',
    'align', 'direction', 'code-block', 'formula', 'video',
    this.props.allowImagePaste ? 'image' : null,
  ].filter((item) => !IsEmpty(item))

  componentDidMount() {
    this.quillRef.getEditor().history.clear();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.images !== this.props.images) {
      this.fetchUpdatedDescription();
    }
  }

  fetchUpdatedDescription = () => {
    this.setState({
      description: this.props.description,
    });
  }

  handleChangeDescription = (description) => {
    this.setState({ description }, () => {
      this.props.onChangeDescription(this.state.description);
    });
  }

  handleChangePlainText = (text) => {
    this.props.onChangePlainText(text);
  }

  onChangeFile(event) {
    event.preventDefault();
    if (this.props.allowUpload) {
      this.props.handleUploadImage(event.target.files);
      event.target.value = '';
    }
  }

  render() {
    return (
      <div className={this.state.instanceId}>
        <input
          type="file"
          id="file"
          ref={inputFile}
          style={{ display: 'none' }}
          accept="image/png, image/jpeg, image/jpg"
          onChange={this.onChangeFile.bind(this)}
        />
        <ReactQuill
          ref={(el) => {
            this.quillRef = el;
          }}
          bounds={`.${this.state.instanceId}`}
          formats={this.acceptedFormats}
          modules={this.modules}
          value={this.state.description}
          onChange={(content, delta, source, editor) => {
            this.handleChangeDescription(content);
            this.handleChangePlainText(editor.getText(content));
          }}
        />
        {!IsEmpty(this.props.minHeight) && (
          <style>
            {`
              .${this.state.instanceId} .ql-editor {
                min-height: ${this.props.minHeight};
              }
            `}
          </style>
        )}
      </div>
    );
  }
}

Editor.propTypes = {
  allowUpload:         PropTypes.bool,
  allowImagePaste:     PropTypes.bool,
  minHeight:           PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  images:              PropTypes.shape(Object),
  description:         PropTypes.string,
  onChangeDescription: PropTypes.func.isRequired,
  onChangePlainText:   PropTypes.func,
  handleUploadImage:   PropTypes.func,
};

Editor.defaultProps = {
  allowUpload:       true,
  allowImagePaste:   true,
  minHeight:         null,
  onChangePlainText: () => {},
  handleUploadImage: () => {},
  images:            [],
  description:       '',
};

export default Editor;
